import { param } from '@/redux/selectors/uiSelectors';
import workflowScreenSelectors from '../redux/selectors/workflowScreenSelectors';
import IconV2 from '@/components/v2/IconV2';
import { MdErrorOutline, FiUser, FiInfo, LiaCubesSolid, FiSlash } from '@/components/react-icons/main';
import { FiStar } from '@/components/react-icons/main/index.esm';
import utilityAppIcons from '../schema/utilityAppIcons';
import { query } from '@/redux/selectors/querySelectors';
import React from 'react';
import setQuery from '@/lib/setQuery.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import DividerV2 from '@/components/v2/DividerV2';
import { ReTriggerWebhook } from './RerunWebhook';
import getErrorInfo from './lib/getErrorInfo';
import selectors from '@/redux/selectors/selectors';
import Tag from '@/components/Tag.jsx';
import useResponsive from '@/hooks/useResponsive';
import { setModalState } from '@/redux/actions/uiActions/modalActions';

const findLoopIndex = (loopTimes, time) => {
  time = parseFloat(time);
  if (!loopTimes) {
    return undefined;
  }
  let index = loopTimes.length;
  for (let i = 0; i < loopTimes.length; i++) {
    if (time < loopTimes[i]) {
      index = i;
      break;
    }
  }
  return index;
};

const LogStep = React.memo(({ stepId, indent, output, timeMachine, meta, steps = [] }) => {
  const workflowId = useArgSelector(param, 'workflowId');
  const activeStepId = useArgSelector(query, 'logStepId');
  const activeLogId = useArgSelector(query, 'logId');
  const time = useArgSelector(query, 'time') || 0;
  let stepApp = useArgSelector(workflowScreenSelectors.activeWorkflowStepAppByStepId, stepId);
  const operationLogs = useArgSelector(selectors.workflowOperationLogsByLogId, activeLogId);
  const { tablet, mobile } = useResponsive();
  const isMobile = tablet || mobile;

  let step = steps.find((step) => step.id === stepId);
  const timeMachineStep = timeMachine?.find((entry) => entry.stepId === stepId);
  const isIfElse = step?.appSlug === 'ifElse';
  const isTrigger = step?.type === 'trigger' || stepId === 'manual-trigger' || stepId === 'function-trigger';
  const isWebhookTrigger = isTrigger && step?.appSlug === 'webhook';
  const activeTime = timeMachine[time];
  const selectedTime = timeMachine[meta?.lastIndex];
  const isSkipped = output?.ran === false;
  const success = output?.response?.success !== false;
  const activeLoopIndex = findLoopIndex(selectedTime?.loopTimes, time);
  const hasNotRun = !selectedTime || output === undefined;
  const retries = output?.response?.meta?.retries;

  const stepOperationLogs = operationLogs?.filter((log) => log?.stepId === stepId || log?.stepId === output?.stepId || log?.stepId === output?.stepInstanceId || log?.stepId === output?._id) || [];
  const errorInfo = getErrorInfo(stepOperationLogs);
  const stepStatus = errorInfo?.status;

  const errorCount = stepOperationLogs?.filter((log) => log?.actionStatus === 'error')?.length || 0;
  const warningCount = stepOperationLogs?.filter((log) => log?.actionStatus === 'warn')?.length || 0;
  const infoCount = stepOperationLogs?.filter((log) => log?.actionStatus === 'info')?.length || 0;

  if (stepId === 'error') {
    step = { message: 'Error', workflowId };
    stepApp = { icon: MdErrorOutline };
  }

  if (stepId === 'incomplete-workflow') {
    step = { message: 'Timeline may be incomplete', workflowId };
    stepApp = { icon: MdErrorOutline };
  }

  if (stepId === 'manual-trigger') {
    step = { message: timeMachineStep?.step?.message, workflowId };
    const data = timeMachineStep?.step?.response?.data?.[0];
    if (data) {
      step.info = data?.email ?? data?.userId;
    }
    stepApp = { icon: FiUser };
  }

  if (stepId === 'function-trigger') {
    step = { message: timeMachineStep?.step?.message, workflowId };
    stepApp = { icon: LiaCubesSolid };
  }

  if (!step) {
    return null;
  }

  const handleStepClick = () => {
    const newTime = meta?.firstTime > time ? meta?.firstTime : time;
    const stepMessage = steps.find((s) => s.id === stepId)?.message || 'Step';

    if (isMobile) {
      setModalState('logStepOutput', { show: true, title: stepMessage });
      setQuery({ time: newTime, logStepId: stepId });
    } else {
      setQuery({ time: newTime, logStepId: stepId });
    }
  };

  const handleErrorClick = (e) => {
    e.stopPropagation();

    const firstError = timeMachine?.find((entry) => entry.stepId === stepId && entry.step?.response?.success === false);

    if (!firstError) {
      return;
    }

    const errorTime = firstError?.index || meta?.firstTime;
    const stepMessage = steps.find((s) => s.id === stepId)?.message || 'Step';

    if (isMobile) {
      setModalState('logStepOutput', { show: true, title: stepMessage });
      setQuery({ time: errorTime, logStepId: stepId });
    } else {
      setQuery({ time: errorTime, logStepId: stepId });
    }
  };

  const getStatusClass = () => {
    if (!selectedTime || hasNotRun || isSkipped) {
      return '';
    }
    if (stepStatus === 'error') {
      return 'failed';
    }
    if (stepStatus === 'warn') {
      return 'warning';
    }
    if (stepStatus === 'info') {
      return 'info';
    }
    if (!success && !!selectedTime) {
      return 'failed';
    }
    if (stepId === 'incomplete-workflow') {
      return 'failed';
    }

    return '';
  };
  return (
    <>
      <div className={`log-step ${getStatusClass()} ${stepId === activeStepId ? 'selected' : ''} ${activeTime?.stepId === stepId ? 'active' : ''} level-${indent} ${hasNotRun ? 'has-not-run' : ''} ${isSkipped ? 'skipped' : ''}`} onClick={handleStepClick} data-step-id={stepId}>
        <div className={'inner-wrapper'}>
          <IconV2 icon={stepApp?.icon || utilityAppIcons[stepApp?.slug] || FiStar} />
          {meta?.stepNumber && <div className={'step-number'}>{meta.stepNumber}</div>}
          <div className={'message'}>{step?.message}</div>
          <div className={'tags'}>
            {isSkipped && (
              <div className={'tag skipped'} title={'This step is configured to be skipped and did not run.'}>
                <IconV2 icon={FiSlash} size={11} />
                Skipped
              </div>
            )}
            {selectedTime?.children > 0 && !isIfElse && <div className={'tag iterate'}>{selectedTime?.children}x</div>}
            {retries && <Tag>{retries} Retries</Tag>}
            {activeLoopIndex !== undefined && (
              <div className={'tag'}>
                {activeLoopIndex} of {selectedTime?.loopTimes?.length}
              </div>
            )}
            {stepStatus === 'warn' && warningCount > 0 ? (
              <div className={'warnings tag'}>{warningCount}</div>
            ) : stepStatus === 'info' && infoCount > 0 ? (
              <div className={'info tag'}>{infoCount}</div>
            ) : errorCount > 0 || meta?.errors > 0 ? (
              <div className={'errors tag'} onClick={handleErrorClick}>
                {errorCount || meta?.errors}
              </div>
            ) : null}
            {isWebhookTrigger && <ReTriggerWebhook webhookStepId={stepId} />}
            {step?.info && <IconV2 className="light-medium info-icon" icon={FiInfo} size={12} title={step.info} />}
          </div>
        </div>
      </div>
      {isTrigger && <DividerV2 style={{ width: '90%', margin: '8px 5% 16px' }} />}
    </>
  );
});

LogStep.displayName = 'LogStep';

export default LogStep;
